import { useMemo } from 'react';
import { Alert } from 'antd';
import { AiSearchLessonsQuery } from '@api/graphql-codegen';
import AiLessonGrid from './AiLessonGrid';
import { presentCards } from './aiSearch';
import styles from './AiSearchResults.module.scss';

type Props = {
  result: AiSearchLessonsQuery['aiSearchLessons'];
  isAUuser: boolean;
};

/**
 * AI search results as one list in op-ai's relevance order (OP-3252), not grouped by grade, so the
 * best match is always first. Each card names the school grades the lesson sits under.
 *
 * Weak matches (OP-3250) come after the strong ones, under their own heading. When nothing strong
 * came back, that is said plainly above the closest lessons, so a teacher knows to try other words.
 */
function AiSearchResults({ result, isAUuser }: Props) {
  const { strong, weak } = useMemo(() => {
    const lessons = result.lessons.filter((lesson) => Boolean(lesson?.lesson?.id));
    return {
      strong: presentCards(lessons.filter((lesson) => !lesson.weak)),
      weak: presentCards(lessons.filter((lesson) => lesson.weak)),
    };
  }, [result.lessons]);

  // The page shows its own "no results" message, the same as for a keyword search.
  if (!strong.length && !weak.length) return null;

  return (
    <div className={styles.results}>
      {result.noStrongMatch ? (
        <Alert
          type="info"
          showIcon
          className={styles.notice}
          message="No strong matches for this search."
          description="These are the closest lessons we found. Try different words, or describe the situation in more detail."
        />
      ) : null}

      {strong.length ? (
        <AiLessonGrid cards={strong} isAUuser={isAUuser} programName="Lesson Library AI search" />
      ) : null}

      {weak.length ? (
        <>
          {strong.length ? (
            <div className={styles.divider} role="separator">
              <span>Less relevant matches</span>
            </div>
          ) : null}
          <AiLessonGrid cards={weak} isAUuser={isAUuser} programName="Lesson Library AI search" />
        </>
      ) : null}
    </div>
  );
}

export default AiSearchResults;
